import { FC, PropsWithChildren, useMemo } from 'react';
import { css, Interpolation, Theme } from '@emotion/react';
import { CallIntelTypes } from '@frontend/api-call-intel';
import { useTranslation } from '@frontend/i18n';
import { useBreakpoint } from '@frontend/responsiveness';
import { theme } from '@frontend/theme';
import { Button, Text } from '@frontend/design-system';
import { CalloutBanner } from '../../../common/components/promo-banners';
import { CalloutBannerArrowPosition } from '../../../common/components/promo-banners/callout-banner';
import { useCallIntelInAppBannerVisibility } from '../../hooks/use-call-intel-in-app-banner-visibility';
import { getInAppAwarenessBannerTrackingId } from '../../tracking-ids';
import { InAppAwarenessPromoSource } from '../../types';

interface TSSRecoveredTagCalloutWrapperProps {
  styles?: Interpolation<Theme>;
  locationId?: string;
  isDemo?: boolean;
  callInsights?: Pick<CallIntelTypes.CallRecord, 'schedulingOutcomeV2' | 'isFieldEdited' | 'appointmentMetadata'>;
  arrowPosition?: CalloutBannerArrowPosition;
}

const COMPONENT_ID: InAppAwarenessPromoSource = 'call-insight-recovered-help-tooltip';

export const TSSRecoveredTagCalloutWrapper: FC<PropsWithChildren<TSSRecoveredTagCalloutWrapperProps>> = ({
  arrowPosition = 'right',
  locationId,
  isDemo,
  callInsights,
  styles,
  children,
}) => {
  const { t } = useTranslation('highValueAdoption', { keyPrefix: 'call-intel' });
  const screenSize = useBreakpoint();
  const isSmallScreen = screenSize === 'xsmall';

  const bannerLocationIds = useMemo(() => (locationId ? [locationId] : []), [locationId]);

  const { ref, handleDismiss, shouldShowBanner, isBetaBanner } = useCallIntelInAppBannerVisibility({
    locationIds: bannerLocationIds,
    isDemo,
    componentId: COMPONENT_ID,
    enabled:
      !!callInsights &&
      !callInsights.isFieldEdited?.isSchedulingOutcomeEdited &&
      callInsights.schedulingOutcomeV2 ===
        CallIntelTypes.SchedulingOutcomeV2Enum.SCHEDULING_OUTCOME_SCHEDULED_POST_CALL &&
      !!callInsights.appointmentMetadata?.length,
    maxVisibilityCount: 1,
    maxInteractionCount: 0,
  });

  if (shouldShowBanner) {
    return (
      <div css={defaultStyles.container}>
        <div onMouseEnter={handleDismiss}>{children}</div>
        <CalloutBanner
          color={isBetaBanner ? 'indigo' : 'seaweed'}
          arrowPosition={isSmallScreen ? 'bottom' : arrowPosition}
          containerStyles={[isSmallScreen ? defaultStyles.calloutPositionSmall : defaultStyles.calloutPosition, styles]}
          description={<Text size='small'>{t('Hover to see the appointments that may be related to this call.')}</Text>}
          innerRef={ref}
          actions={
            <Button
              variant='tertiary'
              size='small'
              trackingId={getInAppAwarenessBannerTrackingId({
                source: COMPONENT_ID,
                isBeta: isBetaBanner,
                action: 'dismiss',
              })}
              onClick={handleDismiss}
              css={defaultStyles.dismissBtn}
            >
              {t('Got it')}
            </Button>
          }
        />
      </div>
    );
  }

  return children;
};

const defaultStyles = {
  container: css`
    position: relative;
    width: auto;
  `,
  calloutPositionSmall: css`
    position: absolute;
    top: 0;
    left: 50%;
    transform: translate(calc(-50% + 8px), -100%);
    z-index: ${theme.zIndex.high};
    width: 200px;
  `,
  calloutPosition: css`
    position: absolute;
    top: 50%;
    left: 0;
    transform: translate(-100%, -50%);
    z-index: ${theme.zIndex.high};
    width: 200px;
  `,
  dismissBtn: css`
    color: ${theme.colors.neutral50};
    padding: 0;
    height: auto;
    font-weight: ${theme.font.weight.regular};
  `,
};
